/**
 * Calculates the correct duration for an absence (Sick or Vacation) 
 * based on the employee's employment type, according to German Labor Laws.
 * 
 * @param {Object} employee - The employee document (must contain employmentType, weeklyHours, maxHours, rolling13WeekAverage).
 * @param {string} date - The date of the absence (YYYY-MM-DD).
 * @param {string} absenceType - 'SICK' or 'VACATION'.
 * @param {Array} shifts - Array of all shifts, used to check if the employee was scheduled.
 * @returns {number} The calculated duration in hours.
 */
export function calculateAbsenceHours({ employee, date, absenceType, shifts, leaveDuration }) {
    const type = employee.employmentType || 'FULL_TIME';

    if (type === 'FULL_TIME') {
        // Fixed employees get their weekly hours divided by 5 (standard 5 day week)
        // Fallback to maxHours (contract hours) if weeklyHours is not set
        const weeklyH = employee.weeklyHours || (employee.maxHours ? employee.maxHours / 4.33 : 0);
        const hoursPerDay = weeklyH / 5;
        return leaveDuration === 'HALF' ? hoursPerDay / 2 : hoursPerDay;
    }

    // Flexible Employees (Mini/Midi-jobs without fixed days)
    if (type === 'MINI_JOB' || type === 'MIDI_JOB') {
        if (absenceType === 'SICK') {
            // 1. Lohnausfallprinzip: Did the employee have a shift scheduled for this day?
            const scheduledShift = shifts.find(
                (s) => s.employeeId === employee.id && s.date === date
            );

            if (scheduledShift) {
                // Calculate the planned hours for this shift
                const startPath = scheduledShift.startTime.split(':');
                const endPath = scheduledShift.endTime.split(':');
                const startDec = parseInt(startPath[0], 10) + parseInt(startPath[1], 10) / 60;
                const endDec = parseInt(endPath[0], 10) + parseInt(endPath[1], 10) / 60;
                const duration = endDec - startDec;
                return duration > 0 ? duration : 0;
            }

            // If no shift is scheduled, fallback to the 13-week average
            return employee.rolling13WeekAverage || 0;
        }

        if (absenceType === 'VACATION') {
            // Referenzprinzip: Vacation always pays the 13-week average
            return employee.rolling13WeekAverage || 0;
        }
    }

    return 0;
}
